import {
  TaasTarifrisikoschluesselVHVV2,
  Tierart,
  Umstellungsstatus,
  VorvertragBaustein,
  WarenkorbProduktToValidate,
  WarenkorbVorvertrag,
  VorbelegungForBeibehaltung,
  VorbelegungBausteinForBeibehaltung,
} from '@ps20/bff-models';
import {
  App,
  areAppsEquivalent,
  compareProductIdsIfExistingAndSpLaufnrNotExists,
  FEVorvertragsnummer,
  formatToBffWirksamkeitsbeginn,
  getSpartenTypeByApp,
  StandaloneProdukt,
  TierartEnum,
  VhvV2Vorbelegungdaten,
  VhvVorbelegungdaten,
  Vorbelegungdaten,
  Vorvertrag,
  VwgV2Vorbelegungdaten,
  VwgVorbelegungdaten,
} from '@ps20-frontend/frontend-models';
import { IAppState } from '@ps20-frontend/apps/dashboard-store';
import { PS20Logger } from '@ps20-frontend/logging';
import {
  selectAlleStandaloneProdukte,
  selectAlleVorvertraege,
  selectUmstellungsstatusForStandaloneProdukt,
  selectUmstellungsstatusForVorvertrag,
} from '../selectors/vorvertragsUebersicht.selector';
import { WarenkorbAngebot } from '../reducers/produkte.reducer';

function mapTierartFrontendToBff(tierart: TierartEnum): Tierart | undefined {
  switch (tierart) {
    case TierartEnum.HUND:
      return Tierart.VRHUND;
    case TierartEnum.KATZE:
      return Tierart.VRKATZE;
    case TierartEnum.PFERD:
      return Tierart.VRPFERD;
    default:
      return undefined;
  }
}

export function mapWarenkorbProdukte(state: IAppState): WarenkorbProduktToValidate[] {
  return state.warenkorb
    ? state.warenkorb
        .filter(warenkorbProdukt => warenkorbProdukt.appId !== App.TGV)
        .map((warenkorbProdukt: WarenkorbAngebot) => ({
          spartenType: getSpartenTypeByApp(warenkorbProdukt.appId),
          angebotsId: warenkorbProdukt.angebotsId,
          versichertePerson: warenkorbProdukt.versichertePerson,
          umgestellteVorvertraege: getUmgestellteVorvertraege(warenkorbProdukt, state),
          ...(warenkorbProdukt.appId === App.TKV_V2 && {
            tierart: warenkorbProdukt.tierart
              ? mapTierartFrontendToBff(warenkorbProdukt.tierart)
              : undefined,
            kurztext: warenkorbProdukt.kurztext,
          }),
        }))
    : [];
}

function getUmgestellteVorvertraege(
  warenkorbProdukt: WarenkorbAngebot,
  state: IAppState,
): WarenkorbVorvertrag[] {
  const isErsatz =
    warenkorbProdukt.vorvertragsNummern && warenkorbProdukt.vorvertragsNummern.length > 0;

  return isErsatz
    ? (warenkorbProdukt.vorvertragsNummern.map(vn => {
        const uebernahmeArtAndVorbelegung = mapVertragsuebernahmeArt(
          vn,
          state,
          warenkorbProdukt.appId,
        );
        return {
          vertragsnummer: vn.vertragsnummer,
          spLaufnummer: vn.spLaufnummer,
          wirksamkeitsbeginn: formatToBffWirksamkeitsbeginn(warenkorbProdukt.wirksamkeitsbeginn),
          umstellungsstatus: uebernahmeArtAndVorbelegung?.vertragStatus,
          vorbelegungForBeibehaltung: uebernahmeArtAndVorbelegung?.vorvertrag,
        };
      }) as Array<WarenkorbVorvertrag>)
    : undefined;
}

function findVorbelegungForApp(
  vorvertrag: Vorvertrag,
  appId: App,
): Vorbelegungdaten | undefined {
  const vorbelegungdaten = vorvertrag.vorbelegungdaten ?? [];
  const match =
    vorbelegungdaten.find(daten => daten.forApp === appId) ??
    vorbelegungdaten.find(daten => areAppsEquivalent(daten.forApp, appId));

  if (!match) {
    PS20Logger.error(
      App.SHOP_DASHBOARD,
      'VORBELEGUNGDATEN_SPARTE_NICHT_GEFUNDEN',
      `Keine Vorbelegungdaten fuer App ${appId} am Vorvertrag ` +
        `${vorvertrag.vertragsnummer}/${vorvertrag.spLaufnummer} gefunden. ` +
        `Vorhandene Sparten: ${vorbelegungdaten.map(daten => daten.forApp).join(', ')}`,
    );
  }

  return match;
}

function mapVertragsuebernahmeArt(
  vorvertragsnummer: FEVorvertragsnummer,
  state: IAppState,
  appId: App,
): { vertragStatus: Umstellungsstatus; vorvertrag?: VorbelegungForBeibehaltung } {
  const alleVorvertraege: Vorvertrag[] = selectAlleVorvertraege(state, true);
  const vorvertrag: Vorvertrag =
    alleVorvertraege && alleVorvertraege.length > 0
      ? alleVorvertraege.find(
          vorvertrag =>
            compareProductIdsIfExistingAndSpLaufnrNotExists(
              vorvertragsnummer.produktIds,
              vorvertrag.produktIds,
              vorvertragsnummer.spLaufnummer,
              vorvertrag.spLaufnummer,
            ) && vorvertragsnummer.vertragsnummer === vorvertrag.vertragsnummer,
        )
      : null;

  const alleStandaloneProdukte: StandaloneProdukt[] = selectAlleStandaloneProdukte(state);
  const standaloneProdukt: StandaloneProdukt =
    alleStandaloneProdukte && alleStandaloneProdukte.length > 0
      ? alleStandaloneProdukte.find(
          vorvertrag =>
            vorvertragsnummer.spLaufnummer === vorvertrag.spLaufnummer &&
            vorvertragsnummer.vertragsnummer === vorvertrag.vertragsnummer,
        )
      : null;

  if (vorvertrag != null) {
    const vorbelegung: Vorbelegungdaten | undefined = findVorbelegungForApp(vorvertrag, appId);
    return {
      vertragStatus: selectUmstellungsstatusForVorvertrag(vorvertrag, state),
      vorvertrag: mapVorbelegung(vorbelegung),
    };
  } else if (standaloneProdukt != null) {
    return {
      vertragStatus: selectUmstellungsstatusForStandaloneProdukt(
        state.warenkorb,
        standaloneProdukt,
      ),
    };
  } else {
    const gesuchteVorvertragsnummer = JSON.stringify({
      vertragsnummer: vorvertragsnummer.vertragsnummer,
      spLaufnummer: vorvertragsnummer.spLaufnummer,
      produktIds: vorvertragsnummer.produktIds,
    });
    const logStandard = alleVorvertraege
      ? alleVorvertraege.map(vertrag =>
          JSON.stringify({
            vertragsnummer: vertrag.vertragsnummer,
            spLaufnummer: vertrag.spLaufnummer,
            produktIds: vertrag.produktIds,
          }),
        )
      : '';
    const logStandalone = alleStandaloneProdukte
      ? alleStandaloneProdukte.map(produkt =>
          JSON.stringify({
            vertragsnummer: produkt.vertragsnummer,
            spLaufnummer: produkt.spLaufnummer,
          }),
        )
      : '';

    PS20Logger.error(
      App.SHOP_DASHBOARD,
      'UMSTELLUNGSSTATUS_ERROR',
      'Der Umstellungsstatus von Warenkorb-Vorvertraege konnte nicht ermittelt werden. ' +
        `Gesucht: ${gesuchteVorvertragsnummer}, Vorvertraege: ${logStandard}, Standalone: ${logStandalone}`,
    );
    return { vertragStatus: Umstellungsstatus.APPID_UNBEKANNT };
  }
}
export function mapVorbelegung(
  vorbelegung: Vorbelegungdaten | undefined,
): VorbelegungForBeibehaltung {
  if (!vorbelegung) {
    return null;
  }
  return {
    aktionsNachlass: vorbelegung.aktionsNachlass || 0,
    nachlaesseFaktor: vorbelegung.nachlaesseFaktor || 0,
    vorteilsrabatt: vorbelegung.vorteilsrabatt || 0,
    nachlassTb: vorbelegung.nachlassTb || 0,
    nachlassRbd: vorbelegung.nachlassRbd || 0,
    erhRisiko: vorbelegung.erhRisiko,
    preisverzeichnis: vorbelegung.preisverzeichnis || 0,
    manuellerNachlass: vorbelegung.manuellerNachlass,
    zuschlaegeFaktor: vorbelegung.zuschlaegeFaktor || 0,
    manuellerZuschlag: vorbelegung.manuellerZuschlag,
    anpassungsfaktor: vorbelegung.anpassungsFaktor || 0,
    bausteinList: vorbelegung.vorvertragBausteinList?.map(
      baustein =>
        ({
          deltafaktor: baustein.deltafaktor,
          bausteinAnpassungsfaktor: baustein.bausteinAnpassungsfaktor,
          fachschluessel: baustein.fachschluessel,
        }) as VorbelegungBausteinForBeibehaltung,
    ),
    ...mapSpartenSpezifischeVorbelegung(vorbelegung),
  };
}

function mapSpartenSpezifischeVorbelegung(
  vorbelegung: Vorbelegungdaten,
): Partial<VorbelegungForBeibehaltung> {
  switch (vorbelegung.forApp) {
    case App.VWG:
    case App.VWG_V2:
      return {
        gleitenderNeuwert: (vorbelegung as VwgVorbelegungdaten | VwgV2Vorbelegungdaten).gisDaten
          ?.gleitenderNeuwert,
      };
    case App.VHV:
    case App.VHV_V2:
      return mapHausratSummen(vorbelegung as VhvVorbelegungdaten | VhvV2Vorbelegungdaten);
    default:
      return {};
  }
}

function findBaustein(
  vorvertragBausteinList: VorvertragBaustein<unknown>[] | undefined,
  ...schluessel: string[]
): VorvertragBaustein<unknown> | undefined {
  return vorvertragBausteinList?.find(baustein =>
    schluessel.includes(String(baustein?.taasTarifrisikoschluessel)),
  );
}

function mapHausratSummen(
  vorbelegung: VhvVorbelegungdaten | VhvV2Vorbelegungdaten,
): Partial<VorbelegungForBeibehaltung> {
  const fahrradBaustein = findBaustein(
    vorbelegung.vorvertragBausteinList,
    TaasTarifrisikoschluesselVHVV2.FAHRRAD,
    TaasTarifrisikoschluesselVHVV2.FAHRRAD_KASKO,
  );
  const tragerisikoBaustein = findBaustein(
    vorbelegung.vorvertragBausteinList,
    TaasTarifrisikoschluesselVHVV2.TRAGERISIKO,
  );
  return {
    versicherungssumme: vorbelegung.versicherungssumme,
    wertsachensumme: vorbelegung.wertsachensumme,
    versicherungssummeRad: fahrradBaustein?.betrag,
    versicherungssummeTrag: tragerisikoBaustein?.betrag,
  };
}
